import React, { useState, useEffect } from 'react';
import { FileText, Trash2, CheckCircle, Search, Filter, RefreshCw, Eye, Flag, Calendar, User, GraduationCap } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Admin Materials Search and Moderation Page
 * Search-based interface for finding and moderating materials across all schools
 */
const MaterialsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [schools, setSchools] = useState([]);
  const [filters, setFilters] = useState({
    materialType: '',
    schoolId: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  // Load schools for filtering
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/schools`);
        if (response.ok) {
          const schoolsData = await response.json();
          setSchools(schoolsData);
        }
      } catch (err) {
        console.error('Error fetching schools:', err);
      }
    };
    fetchSchools();
  }, []);

  // Perform search when query changes
  const performSearch = async (query = searchQuery) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Build search parameters
      const params = new URLSearchParams({
        q: query,
        type: 'materials',
        limit: '50',
        ...(filters.materialType && { materialType: filters.materialType }),
        ...(filters.schoolId && { schoolId: filters.schoolId }),
      });

      // Use the existing search API but need to modify it for admin use
      // For now, we'll search all schools by not providing a specific schoolId
      if (!filters.schoolId) {
        // Search across all schools - we need to make multiple requests or modify the API
        const allSchoolSearches = await Promise.all(
          schools.map(async (school) => {
            const schoolParams = new URLSearchParams({
              q: query,
              type: 'materials',
              schoolId: school.id,
              limit: '20',
              ...(filters.materialType && { materialType: filters.materialType }),
            });
            
            try {
              const response = await fetch(`${process.env.REACT_APP_API_URL}/api/search?${schoolParams}`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
              });
              
              if (response.ok) {
                const data = await response.json();
                return data.materials.map(material => ({
                  ...material,
                  schoolName: school.name,
                  schoolId: school.id,
                }));
              }
            } catch (err) {
              console.error(`Error searching school ${school.name}:`, err);
            }
            return [];
          })
        );

        // Combine and deduplicate results
        const combinedResults = allSchoolSearches.flat();
        const uniqueResults = combinedResults.filter((material, index, self) => 
          index === self.findIndex(m => m.id === material.id)
        );
        
        const filteredResults = uniqueResults;

        setSearchResults(filteredResults.slice(0, 50)); // Limit to 50 results
      } else {
        // Search specific school
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/search?${params}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to search materials');
        }

        const data = await response.json();
        const school = schools.find(s => s.id === filters.schoolId);
        
        const resultsWithSchool = data.materials.map(material => ({
          ...material,
          schoolName: school?.name || 'Unknown',
          schoolId: school?.id,
        }));

        const filteredResults = resultsWithSchool;

        setSearchResults(filteredResults);
      }
    } catch (err) {
      console.error('Error searching materials:', err);
      setError(err.message);
      toast.error('Failed to search materials');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    performSearch();
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    // Re-search with new filters if there's a search query
    if (searchQuery.length >= 2) {
      performSearch();
    }
  };

  // Handle material selection for moderation
  const handleSelectMaterial = async (material) => {
    try {
      // Fetch additional material details for moderation view
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/materials/${material.id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.ok) {
        const detailedMaterial = await response.json();
        setSelectedMaterial({
          ...material,
          ...detailedMaterial,
        });
      } else {
        setSelectedMaterial(material);
      }
    } catch (err) {
      console.error('Error fetching material details:', err);
      setSelectedMaterial(material);
    }
  };

  // Handle material moderation actions
  const handleModerateMaterial = async (materialId, action) => {
    if (!materialId) {
      toast.error('Material ID is missing');
      return;
    }

    try {
      let response;
      
      if (action === 'remove') {
        // Use the regular delete endpoint for removal
        response = await fetch(`${process.env.REACT_APP_API_URL}/api/materials/${materialId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
      } else if (action === 'approve') {
        // Use the moderation endpoint for approval
        response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/materials/${materialId}/moderate`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ action }),
        });
      }

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Moderation failed:', response.status, errorData);
        throw new Error(`Failed to ${action} material: ${response.status}`);
      }

      // Update search results and selected material
      setSearchResults(prev => 
        action === 'remove' 
          ? prev.filter(m => m.id !== materialId)
          : prev.map(m => m.id === materialId ? { ...m, reportCount: 0 } : m)
      );

      if (selectedMaterial?.id === materialId) {
        if (action === 'remove') {
          setSelectedMaterial(null);
        } else {
          setSelectedMaterial(prev => ({ ...prev, reportCount: 0 }));
        }
      }

      toast.success(
        action === 'approve' 
          ? 'Material approved and reports cleared'
          : 'Material removed successfully'
      );
    } catch (err) {
      console.error(`Error ${action}ing material:`, err);
      toast.error(err.message);
    }
  };

  // Clear search and results
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedMaterial(null);
    setFilters({
      materialType: '',
      schoolId: '',
    });
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Search & Moderate Materials
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Search for materials across all schools and manage content moderation
          </p>
        </div>
        
        {(searchQuery || searchResults.length > 0) && (
          <button
            onClick={clearSearch}
            className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            <RefreshCw size={16} className="mr-2" />
            Clear Search
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Search Panel */}
        <div className="lg:col-span-2">
          {/* Search Form */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
            <form onSubmit={handleSearch}>
              <div className="flex gap-3 mb-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search materials by title, description, or content..."
                      className="pl-10 pr-4 py-3 w-full border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={searchQuery.length < 2 || loading}
                  className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </form>

            {/* Filters */}
            <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mb-3"
              >
                <Filter size={16} className="mr-2" />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>

              {showFilters && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Material Type
                    </label>
                    <select
                      value={filters.materialType}
                      onChange={(e) => handleFilterChange('materialType', e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">All Types</option>
                      <option value="notes">Notes</option>
                      <option value="study_guide">Study Guide</option>
                      <option value="practice_materials">Practice Materials</option>
                      <option value="course_resources">Course Resources</option>
                      <option value="group_study">Group Study</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      School
                    </label>
                    <select
                      value={filters.schoolId}
                      onChange={(e) => handleFilterChange('schoolId', e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">All Schools</option>
                      {schools.map((school) => (
                        <option key={school.id} value={school.id}>
                          {school.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Search Results */}
          {error && (
            <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-6">
              <strong className="font-bold">Error: </strong>
              {error}
            </div>
          )}

          {searchQuery.length >= 2 && searchResults.length === 0 && !loading && (
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-8 text-center">
              <FileText size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No materials found
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Try adjusting your search terms or filters
              </p>
            </div>
          )}

          {searchResults.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                  Search Results ({searchResults.length})
                </h2>
              </div>
              
              <div className="space-y-3">
                {searchResults.map((material) => (
                  <div
                    key={material.id}
                    className={`bg-white dark:bg-gray-800 rounded-lg border p-4 cursor-pointer transition-all duration-200 hover:shadow-md ${
                      selectedMaterial?.id === material.id
                        ? 'border-red-500 ring-2 ring-red-200 dark:ring-red-800'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    } ${
                      material.reportCount > 0
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                        : ''
                    }`}
                    onClick={() => handleSelectMaterial(material)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center">
                          <FileText size={20} className="text-gray-400 mr-3 flex-shrink-0" />
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900 dark:text-white truncate">
                              {material.title}
                            </h3>
                            <div className="flex flex-wrap items-center text-sm text-gray-500 dark:text-gray-400 mt-1 space-x-4">
                              <div className="flex items-center">
                                <GraduationCap size={14} className="mr-1" />
                                {material.courseCode}: {material.courseName}
                              </div>
                              <div className="flex items-center">
                                <User size={14} className="mr-1" />
                                {material.userName || 'Unknown'}
                              </div>
                              <div className="flex items-center">
                                <Calendar size={14} className="mr-1" />
                                {formatDate(material.createdAt)}
                              </div>
                            </div>
                            {material.description && (
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 line-clamp-2">
                                {material.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 ml-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          material.type === 'notes' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                          material.type === 'exam' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                          material.type === 'syllabus' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                          'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        }`}>
                          {material.type.charAt(0).toUpperCase() + material.type.slice(1)}
                        </span>
                        {material.reportCount > 0 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                            <Flag size={12} className="mr-1" />
                            {material.reportCount}
                          </span>
                        )}
                        <div className="text-xs text-gray-500">
                          {material.schoolName}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Material Details & Moderation Panel */}
        <div className="lg:col-span-1">
          {selectedMaterial ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 sticky top-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  Material Details
                </h2>
                <button
                  onClick={() => setSelectedMaterial(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    {selectedMaterial.title}
                  </h3>
                  {selectedMaterial.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {selectedMaterial.description}
                    </p>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Course:</span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {selectedMaterial.courseCode}: {selectedMaterial.courseName}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">School:</span>
                    <span className="text-gray-900 dark:text-white">
                      {selectedMaterial.schoolName}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Type:</span>
                    <span className="text-gray-900 dark:text-white">
                      {selectedMaterial.type.charAt(0).toUpperCase() + selectedMaterial.type.slice(1)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Uploaded by:</span>
                    <span className="text-gray-900 dark:text-white">
                      {selectedMaterial.userName || 'Unknown'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Date:</span>
                    <span className="text-gray-900 dark:text-white">
                      {formatDate(selectedMaterial.createdAt)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">File Size:</span>
                    <span className="text-gray-900 dark:text-white">
                      {formatFileSize(selectedMaterial.fileSize)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Reports:</span>
                    <span className={selectedMaterial.reportCount > 0 ? 'text-red-600 font-medium' : 'text-gray-900 dark:text-white'}>
                      {selectedMaterial.reportCount || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Votes:</span>
                    <span className="text-gray-900 dark:text-white">
                      ↑{selectedMaterial.upvotes || 0} ↓{selectedMaterial.downvotes || 0}
                    </span>
                  </div>
                </div>

                <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                  <div className="flex flex-col space-y-3">
                    <a
                      href={selectedMaterial.fileUrl?.startsWith('http') 
                        ? selectedMaterial.fileUrl 
                        : `${process.env.REACT_APP_API_URL}${selectedMaterial.fileUrl}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <Eye size={16} className="mr-2" />
                      View Material
                    </a>
                    
                    <button
                      onClick={() => handleModerateMaterial(selectedMaterial.id, 'approve')}
                      className="inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                    >
                      <CheckCircle size={16} className="mr-2" />
                      Approve & Clear Reports
                    </button>
                    
                    <button
                      onClick={() => handleModerateMaterial(selectedMaterial.id, 'remove')}
                      className="inline-flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                    >
                      <Trash2 size={16} className="mr-2" />
                      Remove Material
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8 text-center">
              <FileText size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-500 dark:text-gray-400 mb-2">
                No Material Selected
              </h3>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Search for materials and click on a result to view details and moderation options
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaterialsPage;